import { createList } from "../lists/list";
import { createPage } from "../pages/page";
import { createHeader, createFooter, createMenuItem } from "../navigation/menu";
import { CurationItem } from "../../types";
import {
    WebsiteThemeLibrary,
    WebsiteStructureTheme,
    type ThemeMenuDefinition,
    type ThemeMenuItemDefinition,
    type ThemePageDefinition,
} from "./websiteThemes";
import { VIMOND_IMAGE_SERVICE_BASE_URL } from "../../constants";
import { callCurationProxy } from "../backend/backendClient";

/**
 * Parameters for creating a website structure
 * @property parentId - ID of the parent list
 * @property title - Title of the website
 * @property alias - Alias of the website
 * @property description - Description of the website
 * @property companyId - Company ID of the website
 * @property publisherId - Publisher ID of the website
 * @property themeBrandColor - Theme brand color of the website
 * @property themeId - ID of the theme to use for the website structure
 * @property logoUrl - URL of the logo to use for the website structure
 */
export type CreateWebsiteStructureParams = {
    parentId: string;
    title: string;
    alias: string;
    description?: string;
    companyId?: string;
    publisherId?: string;
    themeBrandColor?: string;
    themeId?: WebsiteStructureTheme;
    logoUrl?: string | null;
    onProgress?: (value: number) => void;
};

/**
 * Result of creating a website structure
 * @property rootList - Root list of the website structure
 * @property header - Header of the website structure
 * @property footer - Footer of the website structure
 */
export type CreateWebsiteStructureResult = {
    rootList: CurationItem;
    header?: CurationItem | null;
    footer?: CurationItem | null;
};

/**
 * Call the progress callback if it is defined, otherwise do nothing.
 * @param callback
 * @param value
 */
const callProgress = (callback: ((value: number) => void) | undefined, value: number) => {
    if (callback) {
        callback(value);
    }
};

/**
 * Create a website structure
 * @param parentId
 * @param title
 * @param alias
 * @param description
 * @param companyId
 * @param publisherId
 * @param themeBrandColor
 * @param themeId
 * @param logoUrl
 * @param onProgress
 */
export const createWebsiteStructure = async ({
    parentId,
    title,
    alias,
    description,
    companyId,
    publisherId,
    themeBrandColor,
    themeId,
    logoUrl,
    onProgress,
}: CreateWebsiteStructureParams): Promise<CreateWebsiteStructureResult> => {
    const trimmedTitle = title.trim();
    const trimmedAlias = alias.trim();
    const trimmedDescription = description?.trim();
    const trimmedCompanyId = companyId?.trim();
    const trimmedPublisherId = publisherId?.trim();
    const trimmedThemeBrandColor = themeBrandColor?.trim();
    const trimmedLogoUrl = logoUrl?.trim() ?? null;

    if (!parentId?.trim()) {
        throw new Error("createWebsiteStructure requires a valid parentId.");
    }
    if (!trimmedTitle) {
        throw new Error("createWebsiteStructure requires a non-empty title.");
    }
    if (!trimmedAlias) {
        throw new Error("createWebsiteStructure requires a non-empty alias.");
    }

    callProgress(onProgress, 0);

    const selectedThemeId = themeId ?? WebsiteThemeLibrary.defaultTheme();
    const themeDefinition = WebsiteThemeLibrary.get(selectedThemeId);
    const menus: ThemeMenuDefinition[] = themeDefinition.menus ?? [];
    const menuItemCount = menus.reduce((total, menu) => total + menu.items.length, 0);
    const hasLogoUrl = Boolean(trimmedLogoUrl);
    const totalUnits = 1 + themeDefinition.pages.length + 2 + menuItemCount + (hasLogoUrl ? 2 : 0);
    let completedUnits = 0;
    const advanceProgress = () => {
        completedUnits += 1;
        const percentage =
            completedUnits >= totalUnits ? 100 : Math.round((completedUnits / totalUnits) * 100);
        callProgress(onProgress, percentage);
    };

    const rootList = await createList(parentId.trim(), "list", trimmedTitle, trimmedAlias, {
        description: trimmedDescription,
        companyId: trimmedCompanyId,
        publisherId: trimmedPublisherId,
        themeBrandColor: trimmedThemeBrandColor,
    });

    if (!rootList?.listId) {
        throw new Error("Website structure creation failed: listId was not returned from the API.");
    }

    advanceProgress();

    const homePageIndex = themeDefinition.pages.findIndex(
        (page: ThemePageDefinition) => page.title === "Home Page" || page.slug === "/",
    );
    const homePage = homePageIndex >= 0 ? themeDefinition.pages[homePageIndex] : null;
    const otherPages = themeDefinition.pages.filter(
        (_page: ThemePageDefinition, index: number) => index !== homePageIndex,
    );

    for (const page of otherPages) {
        await createPage(rootList.listId, page.title, page.slug);
        advanceProgress();
    }

    const header = await createHeader(rootList.listId);
    advanceProgress();

    const footer = await createFooter(rootList.listId);
    advanceProgress();

    let uploadedLogoImage: { imagePackId?: string; imageUrl?: string } | null = null;

    if (hasLogoUrl && trimmedLogoUrl) {
        const uploadResponse = await callCurationProxy({
            base: "imageUpload",
            path: "/image-uri",
            method: "PUT",
            body: {
                location: "main",
                uri: trimmedLogoUrl,
            },
        });

        if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            throw new Error(`Logo upload failed: ${uploadResponse.status} - ${errorText}`);
        }

        const uploadData = await uploadResponse.json();
        uploadedLogoImage = {
            imagePackId: uploadData?.id,
            imageUrl: uploadData?.id
                ? `${VIMOND_IMAGE_SERVICE_BASE_URL}/api/v2/img/${encodeURIComponent(uploadData.id)}`
                : trimmedLogoUrl ?? undefined,
        };

        advanceProgress();
    }

    if (homePage) {
        await createPage(rootList.listId, homePage.title, homePage.slug);
        advanceProgress();
    }

    if (menus.length) {
        const menuTargets: Record<"Header" | "Footer", CurationItem | null | undefined> = {
            Header: header,
            Footer: footer,
        };

        for (const menu of menus) {
            const parentMenu = menuTargets[menu.layout];
            if (!parentMenu?.listId) {
                continue;
            }

            let current = 0;
            const otherItems = menu.items.filter(
                (item: ThemeMenuItemDefinition) => item.title !== "Home Page" && item.url !== "/",
            );
            for (const item of otherItems) {
                await createMenuItem(parentMenu.listId, item.title, item.url, {
                    index: current,
                });
                current++;
                advanceProgress();
            }

            for (const item of menu.items) {
                const isHomePage = item.title === "Home Page" || item.url === "/";
                if (isHomePage) {
                    await createMenuItem(parentMenu.listId, item.title, item.url, {
                        index: current,
                    });
                    current++;
                    advanceProgress();
                }
            }

            if (parentMenu === header && uploadedLogoImage) {
                await createMenuItem(
                    parentMenu.listId,
                    "Logo",
                    themeDefinition.logoTargetUrl ?? "/",
                    {
                        titleOverride: "logo",
                        imagePackId: uploadedLogoImage.imagePackId,
                        defaultMetadata: {
                            "image-pack-id": uploadedLogoImage.imagePackId,
                        },
                        enMetadata: {
                            imageUrl: uploadedLogoImage.imageUrl,
                        },
                        index: current,
                    },
                );
                advanceProgress();
            }
        }
    }

    return { rootList, header: header ?? null, footer: footer ?? null };
};
